import PropTypes from 'prop-types';
import _ from 'lodash';
import { Box, Table, TableBody, TableHead, useTheme, useMediaQuery } from '@mui/material';
import * as types from 'src/types';
import { Info } from 'src/components/Banners';
import tableStyles from 'styles/table.module.css';
import PayerClaimsHeader from './PayerClaimsHeader';
import ClaimRow from './ClaimRow';
import MobileClaimCard from './MobileClaimCard';

export default function PayerClaims({
  claims,
  criteria,
  updateCriteria,
  noClaimsText,
  isSelectable,
  selected,
  onSelectedChange,
  defaultCaseNumbers = new Set(),
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const groupedClaims = _.groupBy(claims, 'claimantName');

  function isSelected(claimId) {
    return selected.indexOf(claimId) !== -1;
  }

  function selectRow(claimId) {
    let newSelected = [];
    if (selected.indexOf(claimId) === -1) {
      newSelected = newSelected.concat(selected, claimId);
    } else {
      newSelected = selected.filter((item) => item !== claimId);
    }
    onSelectedChange(newSelected);
  }

  function selectAllInGroup(claimantClaims) {
    const allowedClaimIds = claimantClaims.filter((x) => x.isPaymentAllowed === true).map((claim) => claim.claimId);
    onSelectedChange((prev) => _.uniq([...prev, ...allowedClaimIds]));
  }

  function selectNoneInGroup(claimantClaims) {
    const claimIds = claimantClaims.map((claim) => claim.claimId);
    onSelectedChange((prev) => prev.filter((id) => !claimIds.includes(id)));
  }

  if (claims.length === 0) {
    return <Info message={noClaimsText} />;
  }

  return (
    <Box>
      {Object.entries(groupedClaims).map(([claimantName, claimantClaims]) => (
        <Box key={claimantName} sx={{ ...(isMobile ? {} : { padding: '10px 30px 0 30px' }) }}>
          {isMobile ? (
            claimantClaims.map((claim, index) => (
              <MobileClaimCard
                key={claim.claimId}
                claim={claim}
                isFirst={index === 0}
                isSelectable={isSelectable}
                isSelected={isSelected(claim.claimId)}
                onSelect={() => selectRow(claim.claimId)}
                isDefaultRegistered={defaultCaseNumbers.has(claim.claimNumber)}
              />
            ))
          ) : (
            <Box className={tableStyles.motusTableWrap}>
              <Table>
                <TableHead>
                  <PayerClaimsHeader
                    criteria={criteria}
                    updateCriteria={updateCriteria}
                    isSelectable={isSelectable}
                    selectAll={() => selectAllInGroup(claimantClaims)}
                    selectNone={() => selectNoneInGroup(claimantClaims)}
                    selectedCount={claimantClaims.filter((claim) => isSelected(claim.claimId)).length}
                  />
                </TableHead>
                <TableBody>
                  {claimantClaims.map((claim) => (
                    <ClaimRow
                      key={`openClaim${claim.claimId}`}
                      isSelected={isSelected(claim.claimId)}
                      isSelectable={isSelectable}
                      claim={claim}
                      onClick={() => selectRow(claim.claimId)}
                      isDefaultRegistered={defaultCaseNumbers.has(claim.claimNumber)}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

PayerClaims.propTypes = {
  noClaimsText: PropTypes.string.isRequired,
  claims: PropTypes.arrayOf(types.claim).isRequired,
  criteria: types.criteria.isRequired,
  updateCriteria: PropTypes.func.isRequired,
  isSelectable: PropTypes.bool,
};
